import { GymPhotosAdmin } from '../../components/gym-photos/GymPhotosAdmin.js';
import { supportedLocales, translate } from '../../i18n/index.js';
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import type { Gym, GymContentTranslations, OperatingHours } from '@atomfit/shared';
import type { CreateGymInput, UpdateGymInput } from '@atomfit/shared';
import { AdminLayout } from '../../components/layout/AdminLayout.js';
import { CustomSelect, Spinner, Tag } from '../../components/design/AtomFitUI.js';
import { gymsApi } from '../../api/gyms.api.js';
import type { LockDeviceStatus } from '../../api/gyms.api.js';
import { operatingHoursApi } from '../../api/pricing.api.js';
import { adminApi, type FranchiseTenant } from '../../api/admin.api.js';
import { useAuth } from '../../hooks/useAuth.js';
import { getRuntimeConfig } from '../../config/runtime.js';
import { isValidPhoneNumber } from 'react-phone-number-input';

const DAYS = [
  translate('pages.admin.admin_gyms_page.module.days'),
  translate('pages.admin.admin_gyms_page.module.days_2'),
  translate('pages.admin.admin_gyms_page.module.days_3'),
  translate('pages.admin.admin_gyms_page.module.days_4'),
  translate('pages.admin.admin_gyms_page.module.days_5'),
  translate('pages.admin.admin_gyms_page.module.days_6'),
  translate('pages.admin.admin_gyms_page.module.days_7'),
];

let googleMapsPlacesPromise: Promise<{
  PlaceAutocompleteElement: new () => GooglePlaceAutocompleteElement;
}> | null = null;

type ModalState =
  | { type: 'none' }
  | { type: 'create' }
  | { type: 'edit'; gym: Gym }
  | { type: 'delete'; gym: Gym }
  | { type: 'locks'; gym: Gym };

type LockAction = 'lock' | 'unlock' | 'unlatch';

interface GymFormData {
  name: string;
  slug: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  postcode: string;
  country: string;
  lat: string;
  lng: string;
  contactEmail: string;
  contactPhone: string;
  contentTranslations: GymContentTranslations;
  timezone: string;
  capacity: string;
  slotDurationMinutes: string;
  cancellationDeadlineHours: string;
  accessCodeValidBeforeMin: string;
  accessCodeValidAfterMin: string;
  slotBufferMinutes: string;
  nukiSmartlockIds: string;
  nukiRemoteSmartlockId: string;
  lockAlertPhoneNumbers: string;
  lockConfigBase: Record<string, unknown>;
  operatingHours: HoursForm;
  tenantSlug: string;
}

type HoursForm = Array<{
  dayOfWeek: number;
  openTime: string;
  closeTime: string;
  isClosed: boolean;
}>;

interface GoogleAddressComponent {
  long_name?: string;
  short_name?: string;
  longText?: string;
  shortText?: string;
  types: string[];
}

interface GooglePlaceResult {
  address_components?: GoogleAddressComponent[];
  addressComponents?: GoogleAddressComponent[];
  formattedAddress?: string;
  geometry?: {
    location?: GoogleLatLngLike;
  };
  location?: GoogleLatLngLike;
  fetchFields?: (request: { fields: string[] }) => Promise<void>;
}

interface GoogleLatLngLike {
  lat?: number | (() => number);
  lng?: number | (() => number);
  toJSON?: () => { lat?: number; lng?: number };
}

interface GooglePlaceSelectEvent extends Event {
  place?: GooglePlaceResult;
  placePrediction?: {
    toPlace: () => GooglePlaceResult;
  };
  detail?: {
    place?: GooglePlaceResult;
    placePrediction?: {
      toPlace: () => GooglePlaceResult;
    };
  };
}

type GooglePlaceAutocompleteElement = HTMLElement & {
  includedRegionCodes?: string[];
  placeholder?: string;
};

interface GoogleMapsGlobal {
  maps?: {
    importLibrary?: (name: 'places') => Promise<{
      PlaceAutocompleteElement: new () => GooglePlaceAutocompleteElement;
    }>;
    places?: {
      PlaceAutocompleteElement?: new () => GooglePlaceAutocompleteElement;
    };
  };
}

declare global {
  interface Window {
    google?: GoogleMapsGlobal;
    gm_authFailure?: () => void;
  }
}

function defaultHours(): HoursForm {
  return [0, 1, 2, 3, 4, 5, 6].map((dayOfWeek) => ({
    dayOfWeek,
    openTime: dayOfWeek === 0 || dayOfWeek === 6 ? '08:00' : '06:00',
    closeTime: dayOfWeek === 0 || dayOfWeek === 6 ? '20:00' : '22:00',
    isClosed: false,
  }));
}

function createEmptyForm(): GymFormData {
  return {
    name: '',
    slug: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    postcode: '',
    country: 'CZ',
    lat: '',
    lng: '',
    contactEmail: '',
    contactPhone: '',
    contentTranslations: {},
    timezone: 'Europe/Prague',
    capacity: '1',
    slotDurationMinutes: '60',
    cancellationDeadlineHours: '12',
    accessCodeValidBeforeMin: '3',
    accessCodeValidAfterMin: '3',
    slotBufferMinutes: '5',
    nukiSmartlockIds: '',
    nukiRemoteSmartlockId: '',
    lockAlertPhoneNumbers: '',
    lockConfigBase: {},
    operatingHours: defaultHours(),
    tenantSlug: '',
  };
}

function lockConfigRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function nukiIdsFromLockConfig(lockConfig: Record<string, unknown>): string {
  const configuredIds: unknown[] = Array.isArray(lockConfig['nukiSmartlockIds'])
    ? (lockConfig['nukiSmartlockIds'] as unknown[])
    : [];
  const ids = [lockConfig['nukiSmartlockId'], ...configuredIds]
    .map((value) => (typeof value === 'number' || typeof value === 'string' ? String(value) : ''))
    .filter(Boolean);
  return [...new Set(ids)].join(', ');
}

function nukiRemoteIdFromLockConfig(lockConfig: Record<string, unknown>): string {
  const value = lockConfig['nukiRemoteSmartlockId'];
  return typeof value === 'number' || typeof value === 'string' ? String(value) : '';
}

function parsePositiveIntegerList(value: string): number[] {
  return value
    .split(/[,\s;]+/)
    .map((part) => part.trim())
    .filter(Boolean)
    .map(Number)
    .filter((part) => Number.isInteger(part) && part > 0);
}

function hasInvalidPositiveIntegerToken(value: string): boolean {
  return value
    .split(/[,\s;]+/)
    .map((part) => part.trim())
    .filter(Boolean)
    .some((part) => {
      const parsed = Number(part);
      return !Number.isInteger(parsed) || parsed <= 0;
    });
}

function parseOptionalPositiveInteger(value: string): number | undefined {
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  const parsed = Number(trimmed);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : undefined;
}

function parseAlertPhoneNumbers(value: string): string[] {
  return value
    .split(/[,;\n]+/)
    .map((phone) => phone.trim())
    .filter(Boolean);
}

function buildLockConfig(f: GymFormData): Record<string, unknown> {
  const lockConfig = { ...f.lockConfigBase };
  delete lockConfig['nukiSmartlockId'];
  delete lockConfig['nukiSmartlockIds'];
  delete lockConfig['nukiRemoteSmartlockId'];

  const smartlockIds = parsePositiveIntegerList(f.nukiSmartlockIds);
  if (smartlockIds.length === 1) {
    lockConfig['nukiSmartlockId'] = smartlockIds[0];
  } else if (smartlockIds.length > 1) {
    lockConfig['nukiSmartlockIds'] = smartlockIds;
  }

  const remoteSmartlockId = parseOptionalPositiveInteger(f.nukiRemoteSmartlockId);
  if (remoteSmartlockId !== undefined) {
    lockConfig['nukiRemoteSmartlockId'] = remoteSmartlockId;
  }

  return lockConfig;
}

function nukiLockSummary(gym: Gym): string {
  const ids = nukiIdsFromLockConfig(lockConfigRecord(gym.lockConfig));
  return ids ? `Nuki ${ids}` : '-';
}

function hasNukiLocks(gym: Gym): boolean {
  const config = lockConfigRecord(gym.lockConfig);
  return nukiIdsFromLockConfig(config).length > 0 || nukiRemoteIdFromLockConfig(config).length > 0;
}

function connectionStatusLabel(online: boolean | null): string {
  if (online === true)
    return translate('pages.admin.admin_gyms_page.connection_status_label.connected');
  if (online === false)
    return translate('pages.admin.admin_gyms_page.connection_status_label.disconnected');
  return translate('pages.admin.admin_gyms_page.connection_status_label.unknown');
}

function lockStatusLabel(value: string | null): string {
  if (!value) return translate('pages.admin.admin_gyms_page.lock_status_label.unknown');

  const normalized = value
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, ' ');
  const labels: Record<string, string> = {
    '0': translate('pages.admin.admin_gyms_page.labels.unknown'),
    '1': translate('pages.admin.admin_gyms_page.labels.locked_2'),
    '2': translate('pages.admin.admin_gyms_page.labels.2'),
    '3': translate('pages.admin.admin_gyms_page.labels.unlocked_2'),
    '4': translate('pages.admin.admin_gyms_page.labels.4'),
    '5': translate('pages.admin.admin_gyms_page.labels.unlocked_3'),
    '6': translate('pages.admin.admin_gyms_page.labels.unlocked_4'),
    '7': translate('pages.admin.admin_gyms_page.labels.7'),
    '224': translate('pages.admin.admin_gyms_page.labels.unknown_2'),
    '225': translate('pages.admin.admin_gyms_page.labels.unknown_3'),
    '254': translate('pages.admin.admin_gyms_page.labels.254'),
    '255': translate('pages.admin.admin_gyms_page.labels.unknown_4'),
    locked: translate('pages.admin.admin_gyms_page.labels.locked'),
    locking: translate('pages.admin.admin_gyms_page.labels.locking'),
    'motor blocked': translate('pages.admin.admin_gyms_page.labels.motor_blocked'),
    uncalibrated: translate('pages.admin.admin_gyms_page.labels.uncalibrated'),
    undefined: translate('pages.admin.admin_gyms_page.labels.undefined'),
    unlocked: translate('pages.admin.admin_gyms_page.labels.unlocked'),
    "unlocked (lock 'n' go)": translate('pages.admin.admin_gyms_page.labels.unlocked_lock_n_go'),
    unlocking: translate('pages.admin.admin_gyms_page.labels.unlocking'),
    unlatched: translate('pages.admin.admin_gyms_page.labels.unlatched'),
    unlatching: translate('pages.admin.admin_gyms_page.labels.unlatching'),
  };

  return labels[normalized] ?? translate('pages.admin.admin_gyms_page.lock_status_label.unknown_2');
}

function doorStatusLabel(value: string | null): string {
  if (!value) return translate('pages.admin.admin_gyms_page.door_status_label.unknown');

  const normalized = value
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, ' ');
  const labels: Record<string, string> = {
    '0': translate('pages.admin.admin_gyms_page.labels.unknown_5'),
    '1': translate('pages.admin.admin_gyms_page.labels.unknown_6'),
    '2': translate('pages.admin.admin_gyms_page.labels.closed_2'),
    '3': translate('pages.admin.admin_gyms_page.labels.opened_2'),
    '4': translate('pages.admin.admin_gyms_page.labels.unknown_7'),
    '5': translate('pages.admin.admin_gyms_page.labels.5'),
    '16': translate('pages.admin.admin_gyms_page.labels.unknown_8'),
    '240': translate('pages.admin.admin_gyms_page.labels.unknown_9'),
    '255': translate('pages.admin.admin_gyms_page.labels.unknown_10'),
    closed: translate('pages.admin.admin_gyms_page.labels.closed'),
    'door closed': translate('pages.admin.admin_gyms_page.labels.door_closed'),
    'door opened': translate('pages.admin.admin_gyms_page.labels.door_opened'),
    open: translate('pages.admin.admin_gyms_page.labels.open'),
    opened: translate('pages.admin.admin_gyms_page.labels.opened'),
  };

  return labels[normalized] ?? translate('pages.admin.admin_gyms_page.door_status_label.unknown_2');
}

function getApiErrorMessage(err: unknown): string | undefined {
  return (
    (err as { response?: { data?: { message?: string; error?: { message?: string } } } })?.response
      ?.data?.error?.message ??
    (err as { response?: { data?: { message?: string; error?: { message?: string } } } })?.response
      ?.data?.message
  );
}

function batteryLabel(lock: LockDeviceStatus): string {
  const parts = [];
  if (lock.batteryCharge !== null)
    parts.push(
      translate('pages.admin.admin_gyms_page.battery_label.push.copy', {
        value1: lock.batteryCharge,
      }),
    );
  if (lock.batteryCharging)
    parts.push(translate('pages.admin.admin_gyms_page.battery_label.push.copy_2'));
  if (lock.batteryCritical)
    parts.push(translate('pages.admin.admin_gyms_page.battery_label.push.status'));
  return parts.length > 0
    ? parts.join(' · ')
    : translate('pages.admin.admin_gyms_page.battery_label.unknown');
}

function toSlug(name: string) {
  return name
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

function gymToForm(gym: Gym): GymFormData {
  const lockConfig = lockConfigRecord(gym.lockConfig);
  return {
    name: gym.name,
    slug: gym.slug,
    addressLine1: gym.addressLine1,
    addressLine2: gym.addressLine2 ?? '',
    city: gym.city,
    postcode: gym.postcode,
    country: gym.country,
    lat: gym.lat === null ? '' : String(gym.lat),
    lng: gym.lng === null ? '' : String(gym.lng),
    contactEmail: gym.contactEmail ?? '',
    contactPhone: gym.contactPhone ?? '',
    contentTranslations: {
      ...gym.contentTranslations,
      cs: {
        equipmentDescription: gym.equipmentDescription,
        directionsDescription: gym.directionsDescription,
        ...gym.contentTranslations?.cs,
      },
    },
    timezone: gym.timezone,
    capacity: String(gym.capacity),
    slotDurationMinutes: String(gym.slotDurationMinutes),
    cancellationDeadlineHours: String(gym.cancellationDeadlineHours),
    accessCodeValidBeforeMin: String(gym.accessCodeValidBeforeMin),
    accessCodeValidAfterMin: String(gym.accessCodeValidAfterMin),
    slotBufferMinutes: String(gym.slotBufferMinutes),
    nukiSmartlockIds: nukiIdsFromLockConfig(lockConfig),
    nukiRemoteSmartlockId: nukiRemoteIdFromLockConfig(lockConfig),
    lockAlertPhoneNumbers: (gym.lockAlertPhoneNumbers ?? []).join(', '),
    lockConfigBase: lockConfig,
    operatingHours: mergeHours(gym.operatingHours ?? []),
    tenantSlug: gym.tenant?.slug ?? '',
  };
}

function formToCreateInput(f: GymFormData): CreateGymInput {
  return {
    name: f.name,
    slug: f.slug,
    addressLine1: f.addressLine1,
    addressLine2: f.addressLine2 || undefined,
    city: f.city,
    postcode: f.postcode,
    country: f.country,
    lat: optionalNumber(f.lat),
    lng: optionalNumber(f.lng),
    contactEmail: nullableString(f.contactEmail),
    contactPhone: nullableString(f.contactPhone),
    contentTranslations: f.contentTranslations,
    equipmentDescription: nullableString(f.contentTranslations.cs?.equipmentDescription ?? ''),
    directionsDescription: nullableString(f.contentTranslations.cs?.directionsDescription ?? ''),
    timezone: f.timezone,
    capacity: Number(f.capacity),
    slotDurationMinutes: Number(f.slotDurationMinutes),
    cancellationDeadlineHours: Number(f.cancellationDeadlineHours),
    accessCodeValidBeforeMin: Number(f.accessCodeValidBeforeMin),
    accessCodeValidAfterMin: Number(f.accessCodeValidAfterMin),
    slotBufferMinutes: Number(f.slotBufferMinutes),
    lockConfig: buildLockConfig(f),
    lockAlertPhoneNumbers: parseAlertPhoneNumbers(f.lockAlertPhoneNumbers),
    operatingHours: f.operatingHours,
  };
}

function formToUpdateInput(f: GymFormData, tenantId?: string): UpdateGymInput {
  return {
    ...formToCreateInput(f),
    ...(tenantId ? { tenantId } : {}),
  };
}

export function AdminGymsPage() {
  const { role } = useAuth();
  const isPlatform = role === 'PLATFORM_ADMIN';
  const [gyms, setGyms] = useState<Gym[]>([]);
  const [franchises, setFranchises] = useState<FranchiseTenant[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [editorTab, setEditorTab] = useState('general');
  const [contentLocale, setContentLocale] = useState<string>('cs');
  const [modal, setModal] = useState<ModalState>({ type: 'none' });
  const [form, setForm] = useState<GymFormData>(createEmptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [addressAutocompleteError, setAddressAutocompleteError] = useState<string | null>(null);
  const [lockStatuses, setLockStatuses] = useState<LockDeviceStatus[]>([]);
  const [lockLoading, setLockLoading] = useState(false);
  const [lockError, setLockError] = useState<string | null>(null);
  const [lockActioning, setLockActioning] = useState<{
    smartlockId: number;
    action: LockAction;
  } | null>(null);
  const nameRef = useRef<HTMLInputElement>(null);
  const [addressAutocompleteHost, setAddressAutocompleteHost] = useState<HTMLDivElement | null>(
    null,
  );
  const currentEditTenant = modal.type === 'edit' ? modal.gym.tenant : undefined;
  const tenantOptions = useMemo<
    Array<{ id?: string; name: string; slug: string; type?: string }>
  >(() => {
    if (!currentEditTenant || franchises.some((tenant) => tenant.slug === currentEditTenant.slug)) {
      return franchises;
    }
    return [currentEditTenant, ...franchises];
  }, [currentEditTenant, franchises]);
  const showForm = modal.type === 'create' || modal.type === 'edit';
  const hasGoogleMapsApiKey = Boolean(getRuntimeConfig('VITE_GOOGLE_MAPS_API_KEY'));

  const load = useCallback(
    async (p: number) => {
      setLoading(true);
      try {
        const res = await gymsApi.adminList(role ?? '', p);
        setGyms(res.data.data);
        setTotalPages(res.data.meta.totalPages);
      } finally {
        setLoading(false);
      }
    },
    [role],
  );
  const setAddressAutocompleteHostRef = useCallback((node: HTMLDivElement | null) => {
    setAddressAutocompleteHost(node);
  }, []);

  useEffect(() => {
    void load(page);
  }, [load, page]);

  useEffect(() => {
    if (!isPlatform) return;
    let cancelled = false;

    async function loadFranchises() {
      const firstPage = await adminApi.listTenants(1, 100);
      const remainingPages = Array.from(
        { length: Math.max(0, firstPage.data.meta.totalPages - 1) },
        (_, index) => index + 2,
      );
      const remaining = await Promise.all(remainingPages.map((p) => adminApi.listTenants(p, 100)));
      if (cancelled) return;

      setFranchises(
        [firstPage, ...remaining]
          .flatMap((res) => res.data.data)
          .filter((tenant) => tenant.isActive),
      );
    }

    void loadFranchises();
    return () => {
      cancelled = true;
    };
  }, [isPlatform]);

  async function applySelectedPlace(event: GooglePlaceSelectEvent) {
    const place =
      event.place ??
      event.detail?.place ??
      event.placePrediction?.toPlace() ??
      event.detail?.placePrediction?.toPlace();
    if (!place) return;

    await place.fetchFields?.({ fields: ['addressComponents', 'formattedAddress', 'location'] });
    setForm((current) => ({
      ...current,
      ...placeToAddressPatch(place),
    }));
  }

  useEffect(() => {
    const googleMapsApiKey = getRuntimeConfig('VITE_GOOGLE_MAPS_API_KEY');
    if (!showForm || !addressAutocompleteHost || !googleMapsApiKey) return;
    let cancelled = false;
    let element: GooglePlaceAutocompleteElement | undefined;
    setAddressAutocompleteError(null);

    void loadGoogleMapsPlaces(googleMapsApiKey)
      .then(({ PlaceAutocompleteElement }) => {
        if (cancelled) return;

        element = new PlaceAutocompleteElement();
        element.className = 'af-google-place-autocomplete';
        element.style.textTransform = 'none';
        element.includedRegionCodes = [form.country.toLowerCase() || 'cz'];
        element.placeholder = form.addressLine1 || 'Seifertova 1';

        const handleSelect = (event: Event) => {
          void applySelectedPlace(event as GooglePlaceSelectEvent);
        };
        element.addEventListener('gmp-select', handleSelect);
        element.addEventListener('gmp-placeselect', handleSelect);

        addressAutocompleteHost.replaceChildren(element);
      })
      .catch((err: unknown) => {
        // Keep manual address entry available if Google Maps fails to load.
        if (!cancelled) {
          setAddressAutocompleteError(
            err instanceof Error
              ? err.message
              : translate(
                  'pages.admin.admin_gyms_page.admin_gyms_page.set_address_autocomplete_error.error',
                ),
          );
        }
      });

    return () => {
      cancelled = true;
      element?.remove();
    };
  }, [addressAutocompleteHost, form.addressLine1, form.country, showForm]);

  function openCreate() {
    setForm({ ...createEmptyForm(), tenantSlug: franchises[0]?.slug ?? '' });
    setError(null);
    setEditorTab('general');
    setContentLocale('cs');
    setModal({ type: 'create' });
    setTimeout(() => nameRef.current?.focus(), 50);
  }

  function openEdit(gym: Gym) {
    setForm(gymToForm(gym));
    setError(null);
    setEditorTab('general');
    setContentLocale('cs');
    setModal({ type: 'edit', gym });
    setTimeout(() => nameRef.current?.focus(), 50);
  }

  function openDelete(gym: Gym) {
    setModal({ type: 'delete', gym });
  }

  async function loadLockStatuses(gym: Gym) {
    setLockLoading(true);
    setLockError(null);
    try {
      const res = await gymsApi.lockStatuses(gym.id, isPlatform ? gym.tenant?.slug : undefined);
      setLockStatuses(res.data.data);
    } catch (err: unknown) {
      setLockError(
        getApiErrorMessage(err) ??
          translate('pages.admin.admin_gyms_page.load_lock_statuses.set_lock_error.error'),
      );
    } finally {
      setLockLoading(false);
    }
  }

  function openLocks(gym: Gym) {
    setLockStatuses([]);
    setLockError(null);
    setModal({ type: 'locks', gym });
    void loadLockStatuses(gym);
  }

  function closeModal() {
    setModal({ type: 'none' });
  }

  function handleNameChange(value: string) {
    setForm((prev) => ({
      ...prev,
      name: value,
      slug: modal.type === 'create' ? toSlug(value) : prev.slug,
    }));
  }

  async function handleSubmit() {
    setSaving(true);
    setError(null);
    try {
      if (
        hasInvalidPositiveIntegerToken(form.nukiSmartlockIds) ||
        hasInvalidPositiveIntegerToken(form.nukiRemoteSmartlockId)
      ) {
        setError(translate('pages.admin.admin_gyms_page.handle_submit.set_error.lock'));
        return;
      }
      if (
        parseAlertPhoneNumbers(form.lockAlertPhoneNumbers).some(
          (phone) => !isValidPhoneNumber(phone),
        )
      ) {
        setError(translate('pages.admin.admin_gyms_page.handle_submit.set_error.lock_phone'));
        return;
      }

      if (modal.type === 'create') {
        if (!form.tenantSlug) {
          setError(
            translate('pages.admin.admin_gyms_page.handle_submit.set_error.gym_tenant_select'),
          );
          return;
        }
        await gymsApi.create(formToCreateInput(form), form.tenantSlug);
      } else if (modal.type === 'edit') {
        const targetTenant = isPlatform
          ? tenantOptions.find((tenant) => tenant.slug === form.tenantSlug)
          : undefined;
        if (isPlatform && !targetTenant?.id) {
          setError(
            translate('pages.admin.admin_gyms_page.handle_submit.set_error.gym_tenant_select_2'),
          );
          return;
        }
        await gymsApi.update(
          modal.gym.id,
          formToUpdateInput(form, targetTenant?.id),
          isPlatform ? modal.gym.tenant?.slug : undefined,
        );
        await operatingHoursApi.upsert(
          modal.gym.id,
          form.operatingHours,
          isPlatform ? form.tenantSlug : undefined,
        );
      }
      closeModal();
      void load(page);
    } catch (err: unknown) {
      setError(
        getApiErrorMessage(err) ??
          translate('pages.admin.admin_gyms_page.handle_submit.set_error.error_gym_save'),
      );
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (modal.type !== 'delete') return;
    setSaving(true);
    try {
      await gymsApi.delete(modal.gym.id, isPlatform ? modal.gym.tenant?.slug : undefined);
      closeModal();
      void load(page);
    } catch {
      // delete failure is rare; just close
      closeModal();
    } finally {
      setSaving(false);
    }
  }

  async function handleLockAction(lock: LockDeviceStatus, action: LockAction) {
    if (modal.type !== 'locks') return;
    setLockActioning({ smartlockId: lock.smartlockId, action });
    setLockError(null);
    try {
      await gymsApi.lockAction(
        modal.gym.id,
        lock.smartlockId,
        action,
        isPlatform ? modal.gym.tenant?.slug : undefined,
      );
      await loadLockStatuses(modal.gym);
    } catch (err: unknown) {
      setLockError(
        getApiErrorMessage(err) ??
          translate('pages.admin.admin_gyms_page.handle_lock_action.set_lock_error.error_lock'),
      );
    } finally {
      setLockActioning(null);
    }
  }

  const eyebrow =
    role === 'PLATFORM_ADMIN'
      ? translate('pages.admin.admin_gyms_page.admin_gyms_page.eyebrow')
      : translate('pages.admin.admin_gyms_page.admin_gyms_page.eyebrow_2');

  return (
    <AdminLayout
      title={translate('pages.admin.admin_gyms_page.admin_gyms_page.admin_layout.gym')}
      eyebrow={eyebrow}
    >
      {/* toolbar */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 16,
        }}
      >
        <div style={{ fontSize: 13, color: 'var(--af-muted)' }}>
          {loading
            ? translate('pages.admin.admin_gyms_page.admin_gyms_page.div.loading')
            : gyms.length === 1
              ? translate('pages.admin.admin_gyms_page.admin_gyms_page.div.gym', {
                  count: gyms.length,
                })
              : translate('pages.admin.admin_gyms_page.admin_gyms_page.div.gym_2', {
                  count: gyms.length,
                })}
        </div>
        {isPlatform && (
          <button
            className="af-primary-btn"
            style={{ padding: '10px 20px', fontSize: 13 }}
            onClick={openCreate}
          >
            {translate('pages.admin.admin_gyms_page.admin_gyms_page.button_primary_btn.gym_add')}
          </button>
        )}
      </div>

      {/* table */}
      <div className="af-card" style={{ overflow: 'hidden' }}>
        {loading ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: 24,
              color: 'var(--af-muted)',
            }}
          >
            <Spinner /> {translate('pages.admin.admin_gyms_page.admin_gyms_page.div.loading_gym')}
          </div>
        ) : gyms.length === 0 ? (
          <div style={{ padding: '40px 24px', textAlign: 'center', color: 'var(--af-muted)' }}>
            {isPlatform
              ? translate('pages.admin.admin_gyms_page.admin_gyms_page.div.gym_3')
              : translate('pages.admin.admin_gyms_page.admin_gyms_page.div.gym_4')}
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--af-border)' }}>
                {[
                  ...(isPlatform
                    ? [translate('pages.admin.admin_gyms_page.admin_gyms_page.tr.tenant')]
                    : []),
                  translate('pages.admin.admin_gyms_page.admin_gyms_page.tr.copy'),
                  translate('pages.admin.admin_gyms_page.admin_gyms_page.tr.city'),
                  translate('pages.admin.admin_gyms_page.admin_gyms_page.tr.status'),
                  translate('pages.admin.admin_gyms_page.admin_gyms_page.tr.capacity'),
                  translate('pages.admin.admin_gyms_page.admin_gyms_page.tr.slot'),
                  translate('pages.admin.admin_gyms_page.admin_gyms_page.tr.cancellation'),
                  translate('pages.admin.admin_gyms_page.admin_gyms_page.tr.code'),
                  translate('pages.admin.admin_gyms_page.admin_gyms_page.tr.lock'),
                  translate('pages.admin.admin_gyms_page.admin_gyms_page.tr.copy_2'),

                  '',
                ].map((h) => (
                  <th
                    key={h}
                    style={{
                      padding: '12px 16px',
                      textAlign: 'left',
                      fontSize: 10,
                      fontWeight: 800,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      color: 'var(--af-muted)',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {gyms.map((gym, index) => (
                <tr
                  key={gym.id}
                  style={{
                    borderBottom: index < gyms.length - 1 ? '1px solid var(--af-border)' : 'none',
                    transition: 'background 0.1s',
                  }}
                  onMouseEnter={(e) =>
                    ((e.currentTarget as HTMLTableRowElement).style.background = 'var(--af-s2)')
                  }
                  onMouseLeave={(e) =>
                    ((e.currentTarget as HTMLTableRowElement).style.background = '')
                  }
                >
                  {isPlatform && (
                    <td style={{ padding: '14px 16px', color: 'var(--af-muted)' }}>
                      <div style={{ fontWeight: 800, color: 'var(--af-text)' }}>
                        {gym.tenant?.name ?? '-'}
                      </div>
                      <div
                        className="af-mono"
                        style={{ fontSize: 11, color: 'var(--af-dim)', marginTop: 2 }}
                      >
                        {gym.tenant?.slug ?? ''}
                      </div>
                    </td>
                  )}
                  <td style={{ padding: '14px 16px' }}>
                    <div style={{ fontWeight: 800 }}>{gym.name}</div>
                    <div
                      className="af-mono"
                      style={{ fontSize: 11, color: 'var(--af-dim)', marginTop: 2 }}
                    >
                      {gym.slug}
                    </div>
                  </td>
                  <td style={{ padding: '14px 16px', color: 'var(--af-muted)' }}>{gym.city}</td>
                  <td style={{ padding: '14px 16px' }}>
                    {gym.isActive ? (
                      <Tag color="var(--af-success)" bg="rgba(74,222,128,0.1)">
                        {translate('pages.admin.admin_gyms_page.admin_gyms_page.tag.active')}
                      </Tag>
                    ) : (
                      <Tag color="var(--af-danger)" bg="rgba(232,66,26,0.1)">
                        {translate('pages.admin.admin_gyms_page.admin_gyms_page.tag.inactive')}
                      </Tag>
                    )}
                  </td>
                  <td style={{ padding: '14px 16px', color: 'var(--af-muted)' }}>{gym.capacity}</td>
                  <td style={{ padding: '14px 16px', color: 'var(--af-muted)' }}>
                    {gym.slotDurationMinutes}
                  </td>
                  <td style={{ padding: '14px 16px', color: 'var(--af-muted)' }}>
                    {gym.cancellationDeadlineHours}{' '}
                    {translate('pages.admin.admin_gyms_page.admin_gyms_page.td.copy')}
                  </td>
                  <td style={{ padding: '14px 16px', color: 'var(--af-muted)' }}>
                    {'-'}
                    {gym.accessCodeValidBeforeMin}
                    {'/+'}
                    {gym.accessCodeValidAfterMin}{' '}
                    {translate('pages.admin.admin_gyms_page.admin_gyms_page.td.copy_2')}
                  </td>
                  <td
                    className="af-mono"
                    style={{ padding: '14px 16px', color: 'var(--af-muted)', fontSize: 11 }}
                  >
                    {nukiLockSummary(gym)}
                  </td>
                  <td style={{ padding: '14px 16px', color: 'var(--af-muted)' }}>
                    {gym.slotBufferMinutes}{' '}
                    {translate('pages.admin.admin_gyms_page.admin_gyms_page.td.copy_3')}
                  </td>
                  <td style={{ padding: '14px 16px' }}>
                    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                      <button
                        onClick={() => openLocks(gym)}
                        disabled={!hasNukiLocks(gym)}
                        style={{
                          padding: '6px 12px',
                          borderRadius: 6,
                          border: '1px solid var(--af-border-strong)',
                          background: hasNukiLocks(gym) ? 'transparent' : 'var(--af-s2)',
                          color: hasNukiLocks(gym) ? 'var(--af-muted)' : 'var(--af-dim)',
                          fontSize: 12,
                          fontWeight: 700,
                          cursor: hasNukiLocks(gym) ? 'pointer' : 'not-allowed',
                        }}
                      >
                        {translate('pages.admin.admin_gyms_page.admin_gyms_page.button.label')}
                      </button>
                      <button
                        onClick={() => openEdit(gym)}
                        style={{
                          padding: '6px 12px',
                          borderRadius: 6,
                          border: '1px solid var(--af-border-strong)',
                          background: 'transparent',
                          color: 'var(--af-muted)',
                          fontSize: 12,
                          fontWeight: 700,
                          cursor: 'pointer',
                        }}
                      >
                        {translate('pages.admin.admin_gyms_page.admin_gyms_page.button.edit')}
                      </button>
                      <button
                        onClick={() => openDelete(gym)}
                        style={{
                          padding: '6px 12px',
                          borderRadius: 6,
                          border: '1px solid rgba(232,66,26,0.3)',
                          background: 'transparent',
                          color: 'var(--af-danger)',
                          fontSize: 12,
                          fontWeight: 700,
                          cursor: 'pointer',
                        }}
                      >
                        {translate('pages.admin.admin_gyms_page.admin_gyms_page.button.delete')}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* pagination */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 16 }}>
          <button
            className="af-outline-btn"
            style={{ padding: '8px 16px' }}
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
          >
            {translate('pages.admin.admin_gyms_page.admin_gyms_page.button_outline_btn.previous')}
          </button>
          <span style={{ padding: '8px 12px', fontSize: 13, color: 'var(--af-muted)' }}>
            {page} {'/'} {totalPages}
          </span>
          <button
            className="af-outline-btn"
            style={{ padding: '8px 16px' }}
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            {translate('pages.admin.admin_gyms_page.admin_gyms_page.button_outline_btn.next')}
          </button>
        </div>
      )}

      {/* create / edit modal */}
      {showForm && (
        <Overlay onClose={closeModal}>
          <div className="af-card af-gym-form-modal" onClick={(e) => e.stopPropagation()}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 24,
              }}
            >
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800 }}>
                {modal.type === 'create'
                  ? translate('pages.admin.admin_gyms_page.admin_gyms_page.h2.gym_create')
                  : translate('pages.admin.admin_gyms_page.admin_gyms_page.h2.gym_edit')}
              </h2>
              <button
                onClick={closeModal}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--af-muted)',
                  fontSize: 20,
                  cursor: 'pointer',
                  lineHeight: 1,
                }}
              >
                {'×'}
              </button>
            </div>

            <div
              className="af-gym-editor-tabs"
              role="tablist"
              aria-label={translate('gymEditor.tabs')}
            >
              {['general', 'content', 'photos', 'booking', 'access', 'hours'].map(
                (tab, index, tabs) => (
                  <button
                    type="button"
                    role="tab"
                    id={`gym-editor-tab-${tab}`}
                    aria-controls={`gym-editor-panel-${tab}`}
                    aria-selected={editorTab === tab}
                    tabIndex={editorTab === tab ? 0 : -1}
                    key={tab}
                    onClick={() => setEditorTab(tab)}
                    onKeyDown={(event) => {
                      const next =
                        event.key === 'ArrowRight'
                          ? (index + 1) % tabs.length
                          : event.key === 'ArrowLeft'
                            ? (index + tabs.length - 1) % tabs.length
                            : event.key === 'Home'
                              ? 0
                              : event.key === 'End'
                                ? tabs.length - 1
                                : -1;
                      if (next < 0) return;
                      event.preventDefault();
                      setEditorTab(tabs[next]!);
                      document.getElementById(`gym-editor-tab-${tabs[next]}`)?.focus();
                    }}
                  >
                    {translate(`gymEditor.${tab}`)}
                  </button>
                ),
              )}
            </div>
            <div
              className="af-gym-editor-panel"
              role="tabpanel"
              id="gym-editor-panel-general"
              aria-labelledby="gym-editor-tab-general"
              hidden={editorTab !== 'general'}
            >
              {modal.type === 'create' && isPlatform && (
                <label className="af-label">
                  {translate('pages.admin.admin_gyms_page.admin_gyms_page.label_label.tenant')}

                  <CustomSelect
                    className="af-tenant-select"
                    value={form.tenantSlug}
                    onChange={(tenantSlug) => setForm((p) => ({ ...p, tenantSlug }))}
                    placeholder={translate(
                      'pages.admin.admin_gyms_page.admin_gyms_page.custom_select_tenant_select.placeholder_tenant_select',
                    )}
                    options={franchises.map((tenant) => ({
                      value: tenant.slug,
                      label: tenant.name,
                    }))}
                  />
                </label>
              )}
              {modal.type === 'edit' && isPlatform && (
                <label className="af-label">
                  {translate('pages.admin.admin_gyms_page.admin_gyms_page.label_label.tenant_3')}

                  <CustomSelect
                    className="af-tenant-select"
                    value={form.tenantSlug}
                    onChange={(tenantSlug) => setForm((p) => ({ ...p, tenantSlug }))}
                    placeholder={translate(
                      'pages.admin.admin_gyms_page.admin_gyms_page.custom_select_tenant_select.placeholder_tenant_select_2',
                    )}
                    options={tenantOptions.map((tenant) => ({
                      value: tenant.slug,
                      label: tenant.name,
                    }))}
                  />
                </label>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <label className="af-label">
                  {translate('pages.admin.admin_gyms_page.admin_gyms_page.label_label.copy')}

                  <input
                    ref={nameRef}
                    className="af-input"
                    value={form.name}
                    onChange={(e) => handleNameChange(e.target.value)}
                    placeholder={translate(
                      'pages.admin.admin_gyms_page.admin_gyms_page.input_input.placeholder_content',
                    )}
                  />
                </label>
                <label className="af-label">
                  {translate('pages.admin.admin_gyms_page.admin_gyms_page.label_label.copy_2')}

                  <input
                    className="af-input"
                    value={form.slug}
                    onChange={(e) => setForm((p) => ({ ...p, slug: e.target.value }))}
                    placeholder={translate(
                      'pages.admin.admin_gyms_page.admin_gyms_page.input_input.placeholder_content_2',
                    )}
                  />
                </label>
              </div>

              <label className="af-label">
                {translate('pages.admin.admin_gyms_page.admin_gyms_page.label_label.address')}

                {hasGoogleMapsApiKey ? (
                  <div
                    ref={setAddressAutocompleteHostRef}
                    className="af-google-place-autocomplete-host"
                  />
                ) : (
                  <>
                    <input
                      className="af-input"
                      value={form.addressLine1}
                      onChange={(e) =>
                        setForm((p) => ({ ...p, addressLine1: e.target.value, lat: '', lng: '' }))
                      }
                      placeholder={translate(
                        'pages.admin.admin_gyms_page.admin_gyms_page.input_input.placeholder_content_3',
                      )}
                    />

                    <span style={{ color: 'var(--af-dim)', fontSize: 11, marginTop: 6 }}>
                      {translate('pages.admin.admin_gyms_page.admin_gyms_page.span.map')}
                    </span>
                  </>
                )}
                {addressAutocompleteError && (
                  <span style={{ color: 'var(--af-danger)', fontSize: 11, marginTop: 6 }}>
                    {addressAutocompleteError}
                  </span>
                )}
                {form.lat && form.lng && (
                  <span
                    className="af-mono"
                    style={{ color: 'var(--af-dim)', fontSize: 11, marginTop: 6 }}
                  >
                    {translate('pages.admin.admin_gyms_page.admin_gyms_page.span_mono.location')}
                    {Number(form.lat).toFixed(6)}
                    {','} {Number(form.lng).toFixed(6)}
                  </span>
                )}
              </label>

              <label className="af-label">
                {translate('pages.admin.admin_gyms_page.admin_gyms_page.label_label.address_2')}

                <input
                  className="af-input"
                  value={form.addressLine2}
                  onChange={(e) => setForm((p) => ({ ...p, addressLine2: e.target.value }))}
                  placeholder={translate(
                    'pages.admin.admin_gyms_page.admin_gyms_page.input_input.placeholder_content_4',
                  )}
                />
              </label>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <label className="af-label">
                  {translate('pages.admin.admin_gyms_page.admin_gyms_page.label_label.email')}

                  <input
                    className="af-input"
                    type="email"
                    value={form.contactEmail}
                    onChange={(e) => setForm((p) => ({ ...p, contactEmail: e.target.value }))}
                  />
                </label>
                <label className="af-label">
                  {translate('pages.admin.admin_gyms_page.admin_gyms_page.label_label.phone')}

                  <input
                    className="af-input"
                    value={form.contactPhone}
                    onChange={(e) => setForm((p) => ({ ...p, contactPhone: e.target.value }))}
                  />
                </label>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14 }}>
                <label className="af-label">
                  {translate('pages.admin.admin_gyms_page.admin_gyms_page.label_label.city')}

                  <input
                    className="af-input"
                    value={form.city}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, city: e.target.value, lat: '', lng: '' }))
                    }
                    placeholder={translate(
                      'pages.admin.admin_gyms_page.admin_gyms_page.input_input.placeholder_content_5',
                    )}
                  />
                </label>
                <label className="af-label">
                  {translate('pages.admin.admin_gyms_page.admin_gyms_page.label_label.copy_3')}

                  <input
                    className="af-input"
                    value={form.postcode}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, postcode: e.target.value, lat: '', lng: '' }))
                    }
                    placeholder={'130 00'}
                  />
                </label>
                <label className="af-label">
                  {translate('pages.admin.admin_gyms_page.admin_gyms_page.label_label.copy_4')}

                  <input
                    className="af-input"
                    value={form.country}
                    onChange={(e) =>
                      setForm((p) => ({
                        ...p,
                        country: e.target.value.toUpperCase().slice(0, 2),
                        lat: '',
                        lng: '',
                      }))
                    }
                    placeholder={translate(
                      'pages.admin.admin_gyms_page.admin_gyms_page.input_input.placeholder_content_6',
                    )}
                    maxLength={2}
                  />
                </label>
              </div>
            </div>
            <div
              className="af-gym-editor-panel"
              role="tabpanel"
              id="gym-editor-panel-content"
              aria-labelledby="gym-editor-tab-content"
              hidden={editorTab !== 'content'}
            >
              <div
                className="af-gym-editor-languages"
                role="group"
                aria-label={translate('gymEditor.language')}
              >
                {Array.from(
                  new Map([
                    ...supportedLocales.map((locale) => [locale.code, locale.label] as const),
                    ...Object.keys(form.contentTranslations)
                      .filter((code) => !supportedLocales.some((locale) => locale.code === code))
                      .map((code) => [code, code] as const),
                  ]).entries(),
                ).map(([locale, label]) => (
                  <button
                    type="button"
                    key={locale}
                    aria-pressed={contentLocale === locale}
                    onClick={() => setContentLocale(locale)}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <p className="af-gym-editor-help">{translate('gymEditor.languageHelp')}</p>
              <section
                style={{
                  borderTop: '1px solid var(--af-border)',
                  paddingTop: 18,
                  marginTop: 4,
                }}
              >
                <div style={{ fontSize: 15, fontWeight: 800, marginBottom: 14 }}>
                  {translate('pages.admin.admin_gyms_page.admin_gyms_page.div.gym_5')}
                </div>
                <label className="af-label">
                  {translate('pages.admin.admin_gyms_page.admin_gyms_page.label_label.equipment')}

                  <textarea
                    className="af-input"
                    rows={10}
                    maxLength={10000}
                    value={form.contentTranslations[contentLocale]?.equipmentDescription ?? ''}
                    onChange={(e) =>
                      setForm((p) => ({
                        ...p,
                        contentTranslations: {
                          ...p.contentTranslations,
                          [contentLocale]: {
                            ...p.contentTranslations[contentLocale],
                            equipmentDescription: e.target.value,
                          },
                        },
                      }))
                    }
                    placeholder={translate(
                      'pages.admin.admin_gyms_page.admin_gyms_page.textarea_input.placeholder_content',
                    )}
                    style={{ resize: 'vertical', lineHeight: 1.5 }}
                  />
                </label>
                <label className="af-label" style={{ marginTop: 14 }}>
                  {translate('pages.admin.admin_gyms_page.admin_gyms_page.label_label.user')}

                  <textarea
                    className="af-input"
                    rows={7}
                    maxLength={10000}
                    value={form.contentTranslations[contentLocale]?.directionsDescription ?? ''}
                    onChange={(e) =>
                      setForm((p) => ({
                        ...p,
                        contentTranslations: {
                          ...p.contentTranslations,
                          [contentLocale]: {
                            ...p.contentTranslations[contentLocale],
                            directionsDescription: e.target.value,
                          },
                        },
                      }))
                    }
                    placeholder={translate(
                      'pages.admin.admin_gyms_page.admin_gyms_page.textarea_input.placeholder_content_2',
                    )}
                    style={{ resize: 'vertical', lineHeight: 1.5 }}
                  />
                </label>
                <span
                  style={{ display: 'block', color: 'var(--af-dim)', fontSize: 11, marginTop: 8 }}
                >
                  {translate('pages.admin.admin_gyms_page.admin_gyms_page.span.gym_map_address')}
                </span>
              </section>
            </div>
            <div
              className="af-gym-editor-panel"
              role="tabpanel"
              id="gym-editor-panel-photos"
              aria-labelledby="gym-editor-tab-photos"
              hidden={editorTab !== 'photos'}
            >
              {modal.type === 'edit' ? (
                <GymPhotosAdmin
                  key={modal.gym.id}
                  gymId={modal.gym.id}
                  tenantSlug={modal.gym.tenant?.slug}
                />
              ) : (
                <GymPhotosAdmin />
              )}
            </div>
            <div
              className="af-gym-editor-panel"
              role="tabpanel"
              id="gym-editor-panel-booking"
              aria-labelledby="gym-editor-tab-booking"
              hidden={editorTab !== 'booking'}
            >
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14 }}>
                <label className="af-label">
                  {translate('pages.admin.admin_gyms_page.admin_gyms_page.label_label.copy_5')}

                  <input
                    className="af-input"
                    value={form.timezone}
                    onChange={(e) => setForm((p) => ({ ...p, timezone: e.target.value }))}
                    placeholder={translate(
                      'pages.admin.admin_gyms_page.admin_gyms_page.input_input.placeholder_content_7',
                    )}
                  />
                </label>
                <label className="af-label">
                  {translate('pages.admin.admin_gyms_page.admin_gyms_page.label_label.capacity')}

                  <input
                    className="af-input"
                    type="number"
                    min={1}
                    value={form.capacity}
                    onChange={(e) => setForm((p) => ({ ...p, capacity: e.target.value }))}
                  />
                </label>
                <label className="af-label">
                  {translate('pages.admin.admin_gyms_page.admin_gyms_page.label_label.slot')}

                  <CustomSelect
                    value={form.slotDurationMinutes}
                    onChange={(slotDurationMinutes) =>
                      setForm((p) => ({ ...p, slotDurationMinutes }))
                    }
                    options={[30, 45, 60, 90, 120].map((v) => ({
                      value: String(v),
                      label: String(v),
                    }))}
                  />
                </label>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 14 }}>
                <label className="af-label">
                  {translate(
                    'pages.admin.admin_gyms_page.admin_gyms_page.label_label.cancellation',
                  )}

                  <input
                    className="af-input"
                    type="number"
                    min={0}
                    max={720}
                    value={form.cancellationDeadlineHours}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, cancellationDeadlineHours: e.target.value }))
                    }
                  />
                </label>
                <label className="af-label">
                  {translate('pages.admin.admin_gyms_page.admin_gyms_page.label_label.code')}

                  <input
                    className="af-input"
                    type="number"
                    min={0}
                    max={1440}
                    value={form.accessCodeValidBeforeMin}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, accessCodeValidBeforeMin: e.target.value }))
                    }
                  />
                </label>
                <label className="af-label">
                  {translate('pages.admin.admin_gyms_page.admin_gyms_page.label_label.code_2')}

                  <input
                    className="af-input"
                    type="number"
                    min={0}
                    max={1440}
                    value={form.accessCodeValidAfterMin}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, accessCodeValidAfterMin: e.target.value }))
                    }
                  />
                </label>
                <label className="af-label">
                  {translate('pages.admin.admin_gyms_page.admin_gyms_page.label_label.copy_6')}

                  <input
                    className="af-input"
                    type="number"
                    min={0}
                    max={240}
                    value={form.slotBufferMinutes}
                    onChange={(e) => setForm((p) => ({ ...p, slotBufferMinutes: e.target.value }))}
                  />
                </label>
              </div>
            </div>
            <div
              className="af-gym-editor-panel"
              role="tabpanel"
              id="gym-editor-panel-access"
              aria-labelledby="gym-editor-tab-access"
              hidden={editorTab !== 'access'}
            >
              <section
                style={{
                  borderTop: '1px solid var(--af-border)',
                  paddingTop: 18,
                  marginTop: 4,
                }}
              >
                <div style={{ fontSize: 15, fontWeight: 800, marginBottom: 14 }}>
                  {translate('pages.admin.admin_gyms_page.admin_gyms_page.div.lock')}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 14 }}>
                  <label className="af-label">
                    {translate('pages.admin.admin_gyms_page.admin_gyms_page.label_label.lock')}

                    <input
                      className="af-input"
                      inputMode="numeric"
                      value={form.nukiSmartlockIds}
                      onChange={(e) => setForm((p) => ({ ...p, nukiSmartlockIds: e.target.value }))}
                      placeholder={translate(
                        'pages.admin.admin_gyms_page.admin_gyms_page.input_input.placeholder_content_8',
                      )}
                    />
                  </label>
                  <label className="af-label">
                    {translate('pages.admin.admin_gyms_page.admin_gyms_page.label_label.open')}

                    <input
                      className="af-input"
                      inputMode="numeric"
                      value={form.nukiRemoteSmartlockId}
                      onChange={(e) =>
                        setForm((p) => ({ ...p, nukiRemoteSmartlockId: e.target.value }))
                      }
                      placeholder={translate(
                        'pages.admin.admin_gyms_page.admin_gyms_page.input_input.placeholder_content_9',
                      )}
                    />
                  </label>
                </div>
                <label className="af-label" style={{ marginTop: 14 }}>
                  {translate('pages.admin.admin_gyms_page.admin_gyms_page.label_label.sms')}

                  <input
                    className="af-input"
                    inputMode="tel"
                    value={form.lockAlertPhoneNumbers}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, lockAlertPhoneNumbers: e.target.value }))
                    }
                    placeholder={'+420777123456, +420777654321'}
                  />

                  <span style={{ color: 'var(--af-dim)', fontSize: 11 }}>
                    {translate('pages.admin.admin_gyms_page.admin_gyms_page.span.copy')}
                  </span>
                </label>
              </section>
            </div>
            <div
              className="af-gym-editor-panel"
              role="tabpanel"
              id="gym-editor-panel-hours"
              aria-labelledby="gym-editor-tab-hours"
              hidden={editorTab !== 'hours'}
            >
              <section
                style={{
                  borderTop: '1px solid var(--af-border)',
                  paddingTop: 18,
                  marginTop: 4,
                }}
              >
                <div style={{ fontSize: 15, fontWeight: 800, marginBottom: 14 }}>
                  {translate(
                    'pages.admin.admin_gyms_page.admin_gyms_page.div.time_operating_hours',
                  )}
                </div>
                <div style={{ display: 'grid', gap: 9 }}>
                  {form.operatingHours.map((item, index) => (
                    <div
                      key={item.dayOfWeek}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '34px 1fr 1fr 86px',
                        gap: 8,
                        alignItems: 'center',
                      }}
                    >
                      <strong style={{ fontSize: 12 }}>{DAYS[item.dayOfWeek]}</strong>
                      <input
                        className="af-input"
                        type="time"
                        value={item.openTime}
                        disabled={item.isClosed}
                        onChange={(e) =>
                          setForm((p) => ({
                            ...p,
                            operatingHours: updateHours(p.operatingHours, index, {
                              openTime: e.target.value,
                            }),
                          }))
                        }
                      />

                      <input
                        className="af-input"
                        type="time"
                        value={item.closeTime}
                        disabled={item.isClosed}
                        onChange={(e) =>
                          setForm((p) => ({
                            ...p,
                            operatingHours: updateHours(p.operatingHours, index, {
                              closeTime: e.target.value,
                            }),
                          }))
                        }
                      />

                      <label
                        style={{
                          display: 'flex',
                          gap: 6,
                          alignItems: 'center',
                          fontSize: 12,
                          color: 'var(--af-muted)',
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={item.isClosed}
                          onChange={(e) =>
                            setForm((p) => ({
                              ...p,
                              operatingHours: updateHours(p.operatingHours, index, {
                                isClosed: e.target.checked,
                              }),
                            }))
                          }
                        />{' '}
                        {translate('pages.admin.admin_gyms_page.admin_gyms_page.label.close')}
                      </label>
                    </div>
                  ))}
                </div>
              </section>
            </div>

            {error && (
              <div
                style={{
                  marginTop: 14,
                  padding: '10px 14px',
                  borderRadius: 8,
                  background: 'rgba(232,66,26,0.1)',
                  border: '1px solid rgba(232,66,26,0.3)',
                  color: 'var(--af-danger)',
                  fontSize: 13,
                }}
              >
                {error}
              </div>
            )}

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
              <button
                className="af-outline-btn"
                style={{ padding: '10px 20px' }}
                onClick={closeModal}
                disabled={saving}
              >
                {translate('pages.admin.admin_gyms_page.admin_gyms_page.button_outline_btn.cancel')}
              </button>
              <button
                className="af-primary-btn"
                style={{ padding: '10px 24px', fontSize: 14 }}
                onClick={() => void handleSubmit()}
                disabled={saving}
              >
                {saving ? (
                  <Spinner />
                ) : modal.type === 'create' ? (
                  translate('pages.admin.admin_gyms_page.admin_gyms_page.button_primary_btn.create')
                ) : (
                  translate('pages.admin.admin_gyms_page.admin_gyms_page.button_primary_btn.save')
                )}
              </button>
            </div>
          </div>
        </Overlay>
      )}

      {/* lock status / control */}
      {modal.type === 'locks' && (
        <Overlay onClose={closeModal}>
          <div
            className="af-card"
            style={{ width: '100%', maxWidth: 620, padding: 28 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 16,
                marginBottom: 18,
              }}
            >
              <div>
                <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800 }}>
                  {translate('pages.admin.admin_gyms_page.admin_gyms_page.h2.heading')}
                </h2>
                <div style={{ marginTop: 4, color: 'var(--af-muted)', fontSize: 13 }}>
                  {modal.gym.name}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  className="af-outline-btn"
                  style={{
                    width: 36,
                    height: 36,
                    padding: 0,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                  onClick={() => void loadLockStatuses(modal.gym)}
                  disabled={lockLoading || lockActioning !== null}
                  aria-label={translate(
                    'pages.admin.admin_gyms_page.admin_gyms_page.button_outline_btn.aria_label_content',
                  )}
                  title={translate(
                    'pages.admin.admin_gyms_page.admin_gyms_page.button_outline_btn.status_refresh',
                  )}
                >
                  {lockLoading ? <Spinner /> : <RefreshCw size={18} aria-hidden="true" />}
                </button>
                <button
                  onClick={closeModal}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--af-muted)',
                    fontSize: 20,
                    cursor: 'pointer',
                    lineHeight: 1,
                  }}
                >
                  {'×'}
                </button>
              </div>
            </div>

            {lockError && (
              <div
                style={{
                  marginBottom: 14,
                  padding: '10px 14px',
                  borderRadius: 8,
                  background: 'rgba(232,66,26,0.1)',
                  border: '1px solid rgba(232,66,26,0.3)',
                  color: 'var(--af-danger)',
                  fontSize: 13,
                }}
              >
                {lockError}
              </div>
            )}

            <div style={{ display: 'grid', gap: 10 }}>
              {lockLoading && lockStatuses.length === 0 ? (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    color: 'var(--af-muted)',
                  }}
                >
                  <Spinner />{' '}
                  {translate('pages.admin.admin_gyms_page.admin_gyms_page.div.loading_2')}
                </div>
              ) : lockStatuses.length === 0 ? (
                <div style={{ color: 'var(--af-muted)', fontSize: 13 }}>
                  {translate('pages.admin.admin_gyms_page.admin_gyms_page.div.gym_6')}
                </div>
              ) : (
                lockStatuses.map((lock) => (
                  <div
                    key={lock.smartlockId}
                    style={{
                      border: '1px solid var(--af-border)',
                      borderRadius: 8,
                      padding: 14,
                      display: 'grid',
                      gridTemplateColumns: 'minmax(0, 1fr)',
                      gap: 14,
                    }}
                  >
                    <div>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <strong>
                          {lock.name ??
                            translate('pages.admin.admin_gyms_page.admin_gyms_page.strong.lock', {
                              value1: lock.smartlockId,
                            })}
                        </strong>
                        <span className="af-mono" style={{ color: 'var(--af-dim)', fontSize: 11 }}>
                          {lock.smartlockId}
                        </span>
                      </div>
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
                          gap: 10,
                          marginTop: 12,
                          fontSize: 12,
                          color: 'var(--af-muted)',
                        }}
                      >
                        <div>
                          <div style={{ color: 'var(--af-dim)', marginBottom: 3 }}>
                            {translate('pages.admin.admin_gyms_page.admin_gyms_page.div.copy')}
                          </div>
                          <strong style={{ color: 'var(--af-text)' }}>
                            {connectionStatusLabel(lock.online)}
                          </strong>
                        </div>
                        <div>
                          <div style={{ color: 'var(--af-dim)', marginBottom: 3 }}>
                            {translate('pages.admin.admin_gyms_page.admin_gyms_page.div.lock_2')}
                          </div>
                          <strong style={{ color: 'var(--af-text)' }}>
                            {lockStatusLabel(lock.lockState)}
                          </strong>
                        </div>
                        <div>
                          <div style={{ color: 'var(--af-dim)', marginBottom: 3 }}>
                            {translate('pages.admin.admin_gyms_page.admin_gyms_page.div.door')}
                          </div>
                          <strong style={{ color: 'var(--af-text)' }}>
                            {doorStatusLabel(lock.doorState)}
                          </strong>
                        </div>
                        <div>
                          <div style={{ color: 'var(--af-dim)', marginBottom: 3 }}>
                            {translate('pages.admin.admin_gyms_page.admin_gyms_page.div.battery')}
                          </div>
                          <strong
                            style={{
                              color:
                                lock.batteryCritical || lock.keypadBatteryCritical
                                  ? 'var(--af-danger)'
                                  : 'var(--af-text)',
                            }}
                          >
                            {batteryLabel(lock)}
                          </strong>
                          {lock.keypadBatteryCritical && (
                            <div style={{ color: 'var(--af-danger)', marginTop: 3 }}>
                              {translate(
                                'pages.admin.admin_gyms_page.admin_gyms_page.div.battery_2',
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                        gap: 8,
                      }}
                    >
                      <button
                        className="af-primary-btn"
                        style={{ padding: '8px 14px', fontSize: 13 }}
                        onClick={() => void handleLockAction(lock, 'unlock')}
                        disabled={lockActioning !== null}
                      >
                        {lockActioning?.smartlockId === lock.smartlockId &&
                        lockActioning.action === 'unlock' ? (
                          <Spinner />
                        ) : (
                          translate(
                            'pages.admin.admin_gyms_page.admin_gyms_page.button_primary_btn.label',
                          )
                        )}
                      </button>
                      <button
                        className="af-primary-btn"
                        style={{ padding: '8px 14px', fontSize: 13 }}
                        onClick={() => void handleLockAction(lock, 'lock')}
                        disabled={lockActioning !== null}
                      >
                        {lockActioning?.smartlockId === lock.smartlockId &&
                        lockActioning.action === 'lock' ? (
                          <Spinner />
                        ) : (
                          translate(
                            'pages.admin.admin_gyms_page.admin_gyms_page.button_primary_btn.label_2',
                          )
                        )}
                      </button>
                      <button
                        className="af-primary-btn"
                        style={{ padding: '8px 14px', fontSize: 13 }}
                        onClick={() => void handleLockAction(lock, 'unlatch')}
                        disabled={lockActioning !== null}
                      >
                        {lockActioning?.smartlockId === lock.smartlockId &&
                        lockActioning.action === 'unlatch' ? (
                          <Spinner />
                        ) : (
                          translate(
                            'pages.admin.admin_gyms_page.admin_gyms_page.button_primary_btn.open',
                          )
                        )}
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </Overlay>
      )}

      {/* delete confirm */}
      {modal.type === 'delete' && (
        <Overlay onClose={closeModal}>
          <div
            className="af-card"
            style={{ width: '100%', maxWidth: 400, padding: 28 }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ margin: '0 0 10px', fontSize: 18, fontWeight: 800 }}>
              {translate('pages.admin.admin_gyms_page.admin_gyms_page.h2.gym_delete')}
            </h2>
            <p
              style={{
                margin: '0 0 20px',
                color: 'var(--af-muted)',
                fontSize: 14,
                lineHeight: 1.6,
              }}
            >
              {translate('pages.admin.admin_gyms_page.admin_gyms_page.p.gym')}

              <strong style={{ color: 'var(--af-text)' }}>{modal.gym.name}</strong>
              {translate('pages.admin.admin_gyms_page.admin_gyms_page.p.back_inactive')}
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button
                className="af-outline-btn"
                style={{ padding: '10px 20px' }}
                onClick={closeModal}
                disabled={saving}
              >
                {translate(
                  'pages.admin.admin_gyms_page.admin_gyms_page.button_outline_btn.cancel_2',
                )}
              </button>
              <button
                onClick={() => void handleDelete()}
                disabled={saving}
                style={{
                  padding: '10px 20px',
                  borderRadius: 8,
                  border: '1px solid rgba(232,66,26,0.4)',
                  background: 'rgba(232,66,26,0.12)',
                  color: 'var(--af-danger)',
                  fontSize: 14,
                  fontWeight: 800,
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                {saving ? (
                  <Spinner />
                ) : (
                  translate('pages.admin.admin_gyms_page.admin_gyms_page.button.delete_2')
                )}
              </button>
            </div>
          </div>
        </Overlay>
      )}
    </AdminLayout>
  );
}

function nullableString(value: string) {
  const trimmed = value.trim();
  return trimmed.length ? trimmed : null;
}

function optionalNumber(value: string) {
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function loadGoogleMapsPlaces(apiKey: string) {
  if (window.google?.maps?.places?.PlaceAutocompleteElement) {
    return Promise.resolve({
      PlaceAutocompleteElement: window.google.maps.places.PlaceAutocompleteElement,
    });
  }
  if (googleMapsPlacesPromise) return googleMapsPlacesPromise;

  googleMapsPlacesPromise = new Promise<{
    PlaceAutocompleteElement: new () => GooglePlaceAutocompleteElement;
  }>((resolve, reject) => {
    const callbackName = `__atomfitGoogleMapsPlacesLoaded_${Date.now()}`;
    const callbacks = window as unknown as Window & Record<string, (() => void) | undefined>;
    const previousAuthFailure = window.gm_authFailure;
    let settled = false;
    function cleanup() {
      delete callbacks[callbackName];
      if (previousAuthFailure) window.gm_authFailure = previousAuthFailure;
      else delete window.gm_authFailure;
    }
    function finish(error?: Error) {
      if (settled) return;
      settled = true;
      cleanup();
      if (error) reject(error);
      else {
        void window.google?.maps
          ?.importLibrary?.('places')
          .then((library) => resolve(library))
          .catch(() => reject(new Error('Google Places API (New) is unavailable')));
      }
    }
    window.gm_authFailure = () => {
      previousAuthFailure?.();
      finish(new Error('Google Maps key is not authorized for this origin or API.'));
    };

    const existing = document.querySelector<HTMLScriptElement>(
      'script[data-atomfit-google-maps="places"]',
    );
    if (existing) {
      if (window.google?.maps?.importLibrary) {
        finish();
        return;
      }
      existing.addEventListener(
        'load',
        () => {
          if (window.google?.maps?.importLibrary) finish();
          else finish(new Error('Google Maps JavaScript API is unavailable'));
        },
        { once: true },
      );
      existing.addEventListener('error', () => finish(new Error('Google Maps failed to load')), {
        once: true,
      });
      return;
    }

    callbacks[callbackName] = () => {
      if (window.google?.maps?.importLibrary) finish();
      else finish(new Error('Google Maps JavaScript API is unavailable'));
    };

    const script = document.createElement('script');
    script.dataset['atomfitGoogleMaps'] = 'places';
    script.async = true;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&v=weekly&loading=async&callback=${callbackName}`;
    script.onerror = () => {
      finish(new Error('Google Maps failed to load'));
    };
    document.head.appendChild(script);
  });

  return googleMapsPlacesPromise;
}

function componentValue(
  components: GoogleAddressComponent[],
  type: string,
  name: 'long_name' | 'short_name' = 'long_name',
) {
  const component = components.find((item) => item.types.includes(type));
  if (!component) return '';
  if (name === 'short_name') return component.short_name ?? component.shortText ?? '';
  return component.long_name ?? component.longText ?? '';
}

function coordinateValue(value: number | (() => number) | undefined) {
  return typeof value === 'function' ? value() : value;
}

function coordinatesFromLocation(location: GoogleLatLngLike | undefined) {
  if (!location) return null;
  const json = location.toJSON?.();
  const lat = coordinateValue(location.lat) ?? json?.lat;
  const lng = coordinateValue(location.lng) ?? json?.lng;

  return typeof lat === 'number' && typeof lng === 'number' ? { lat, lng } : null;
}

function placeToAddressPatch(place: GooglePlaceResult): Partial<GymFormData> {
  const components = place.addressComponents ?? place.address_components ?? [];
  const streetNumber = componentValue(components, 'street_number');
  const route = componentValue(components, 'route');
  const city =
    componentValue(components, 'locality') ||
    componentValue(components, 'postal_town') ||
    componentValue(components, 'administrative_area_level_2');
  const postcode = componentValue(components, 'postal_code');
  const country = componentValue(components, 'country', 'short_name');
  const coordinates = coordinatesFromLocation(place.location ?? place.geometry?.location);
  const fallbackAddress = place.formattedAddress?.split(',')[0]?.trim();
  const addressLine1 = [route, streetNumber].filter(Boolean).join(' ') || fallbackAddress;
  const patch: Partial<GymFormData> = {};

  if (addressLine1) patch.addressLine1 = addressLine1;
  if (city) patch.city = city;
  if (postcode) patch.postcode = postcode;
  if (country) patch.country = country.toUpperCase().slice(0, 2);
  if (coordinates) {
    patch.lat = String(coordinates.lat);
    patch.lng = String(coordinates.lng);
  }

  return patch;
}

function mergeHours(existing: OperatingHours[]): HoursForm {
  return defaultHours().map((fallback) => {
    const item = existing.find((h) => h.dayOfWeek === fallback.dayOfWeek);
    return item
      ? {
          dayOfWeek: item.dayOfWeek,
          openTime: item.openTime,
          closeTime: item.closeTime,
          isClosed: item.isClosed,
        }
      : fallback;
  });
}

function updateHours(
  hours: HoursForm,
  index: number,
  patch: Partial<HoursForm[number]>,
): HoursForm {
  return hours.map((item, i) => (i === index ? { ...item, ...patch } : item));
}

function Overlay({ children, onClose }: { children: React.ReactNode; onClose: () => void }) {
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100,
        padding: 24,
      }}
    >
      {children}
    </div>
  );
}
